import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { addToEDTPast } from '@teetsh/app/src/queries/edt/historyAtoms';
import { EDT_KEY } from '@teetsh/app/src/queries/edt/useEDTs';
import {
  EDTEvent,
  EmploiDuTemps,
  EventTemplateApi,
} from '@teetsh/app/src/types/edt';
import { RepartitionItem } from '@teetsh/app/src/types/event';
import request from '@teetsh/app/src/utils/request';
import differenceInMinutes from '@teetsh/app/src/utils/time/differenceInMinutes';
import formatISO from '@teetsh/app/src/utils/time/formatISO';
import serializeDateToServer from '@teetsh/app/src/utils/time/serializeDateToServer';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { serializeEventTemplate } from './serializer';

export interface UpdateEdtSeanceView {
  id: string;
  lasting: number;
  order: number;
  type: string;
  atelier: boolean;
  skipFromVolume: boolean;
  start: string;
  tagId?: string;
  title: string;
  classroomIds: string[];
  repartition?: RepartitionItem[];
  eventTemplate?: EventTemplateApi;
}

function edtSeanceToUpdateEdtSeanceView(
  edtSeance: EDTEvent,
  tz: string,
): UpdateEdtSeanceView {
  return {
    atelier: edtSeance.atelier,
    classroomIds: edtSeance.classroomIds,
    id: edtSeance.id,
    lasting: differenceInMinutes(edtSeance.end, edtSeance.start),
    order: edtSeance.order,
    repartition: edtSeance.repartition,
    skipFromVolume: edtSeance.skipFromVolume,
    start: formatISO(serializeDateToServer(edtSeance.start, tz)),
    tagId: edtSeance.tag?.id,
    title: edtSeance.title,
    type: edtSeance.type,
    eventTemplate: edtSeance.eventTemplate
      ? serializeEventTemplate(edtSeance.eventTemplate)
      : undefined,
  };
}

function updateEdtSeance({
  schoolyearId,
  schoolId,
  edtSeance,
  tz,
}: {
  schoolyearId: string;
  schoolId: string;
  edtSeance: EDTEvent;
  tz: string;
}) {
  const requestURL = `${
    import.meta.env.VITE_API_URL
  }/schoolyears/${schoolyearId}/schools/${schoolId}/edt-seances/${
    edtSeance.id
  }`;
  return request(requestURL, {
    method: 'PATCH',
    body: edtSeanceToUpdateEdtSeanceView(edtSeance, tz),
  });
}

export function useUpdateEdtSeance(
  edtId: string,
  disableUndoRedo: boolean = false,
) {
  const { notifyError } = useNotification();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { t } = useTranslation(['emploiDuTemps']);
  const user = useUser();
  const queryClient = useQueryClient();
  const [, addToPast] = useAtom(addToEDTPast);

  const { mutateAsync } = useMutation({
    mutationFn: (edtSeance: EDTEvent) =>
      updateEdtSeance({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        tz: user.location,
        edtSeance,
      }),
    onMutate: (edtSeance: EDTEvent) => {
      if (!disableUndoRedo) {
        const oldEDT = queryClient.getQueryData<EmploiDuTemps>([
          schoolyear.id,
          school.id,
          EDT_KEY,
          edtId,
        ]);
        if (oldEDT) {
          addToPast({ seances: oldEDT.events, edtId: oldEDT.id });
        }
      }

      queryClient.setQueryData<EmploiDuTemps>(
        [schoolyear.id, school.id, EDT_KEY, edtId],
        (oldEDT: EmploiDuTemps) => {
          const oldEvents = oldEDT?.events || [];
          return {
            ...oldEDT,
            events: oldEvents.map(evt => {
              if (evt.id === edtSeance.id) {
                return edtSeance;
              }
              return evt;
            }),
          };
        },
      );
    },
    onError: (err, edtSeance) => {
      notifyError({
        title: t('emploiDuTemps:messages.seance.updateError'),
      });
      Sentry.withScope(scope => {
        Sentry.captureException(err);
        scope.setExtra('seance', edtSeance);
      });
    },
  });
  return mutateAsync;
}
